import { useEffect, useRef, useState } from 'react';

/**
 * Measure an element's height and keep it in sync with resizes /
 * DOM changes (chip row appearing, drawer collapsing, etc.). Useful for
 * passing a live height to sibling components — e.g. offsetHeader on
 * antd Table sticky so the table header sticks BELOW a sticky toolbar
 * of variable height.
 */
export function useElementHeight<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setHeight(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return [ref, height];
}
